///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { Injectable, NgZone } from '@angular/core';
import { MapConfigVM } from '../generated/shared-types.model';
import { HttpClient } from '@angular/common/http';
import { map, Observable, of, switchMap, tap } from 'rxjs';
import { E2eTestUtils } from '../../testing/e2e-test-utils';
import { isDefined } from '@siklu/sui-lib/utils';

@Injectable({ providedIn: 'root' })
export class GoogleMapsConfig {
    public static readonly GMAPS_INIT_FUNCTION = 'dynamicGoogleMapsLoader';
    private readonly libraries = ['maps', 'geometry', 'drawing', 'places', 'marker'];
    private readonly element: HTMLElement;

    private readonly mapLatitude = 0;
    private readonly mapLongitude = 0;
    private readonly mapZoomLevel = 2;

    private mapConfig: MapConfigVM;
    private map: google.maps.Map;

    constructor(
        private readonly http: HttpClient,
        private readonly ngZone: NgZone
    ) {
        this.element = document.createElement('div');
        this.element.setAttribute('id', 'map');
        this.element.setAttribute('style', 'height: 100%;');
    }

    getMapElement(): HTMLElement {
        return this.element;
    }

    getMap(): Observable<google.maps.Map> {
        return this.loadJsLibrary().pipe(
            map(() => {
                if (!this.map) {
                    this.createMapObject();
                }
                this.resetMapConfigToDefaults();
                return this.map;
            })
        );
    }

    loadJsLibrary(): Observable<boolean> {
        if (isDefined(this.mapConfig) && isDefined(window.google)) {
            return of(true);
        }

        return this.http.get<MapConfigVM>('/api/topology/map-config').pipe(
            tap((mapConfig) => {
                this.mapConfig = mapConfig;
                window[GoogleMapsConfig.GMAPS_INIT_FUNCTION]({
                    key: mapConfig.key,
                    v: mapConfig.version
                });
            }),
            switchMap(() => Promise.all(this.libraries.map((library) => google.maps.importLibrary(library)))),
            map(() => true)
        );
    }

    private createMapObject(): void {
        this.ngZone.runOutsideAngular(() => {
            this.map = new google.maps.Map(this.element, {
                center: { lat: this.mapLatitude, lng: this.mapLongitude },
                zoom: this.mapZoomLevel,
                mapId: this.mapConfig.mapId,
                fullscreenControl: false,
                controlSize: 30,
                mapTypeControlOptions: {
                    style: google.maps.MapTypeControlStyle.HORIZONTAL_BAR,
                    position: google.maps.ControlPosition.RIGHT_TOP,
                    mapTypeIds: ['roadmap', 'hybrid']
                },
                zoomControlOptions: {
                    position: google.maps.ControlPosition.RIGHT_BOTTOM
                },
                rotateControlOptions: {
                    position: google.maps.ControlPosition.RIGHT_BOTTOM
                },
                streetViewControlOptions: {
                    position: google.maps.ControlPosition.RIGHT_BOTTOM
                }
            });

            const streetViewPanorama = this.map.getStreetView();
            streetViewPanorama.setOptions({
                fullscreenControl: false,
                addressControl: true,
                addressControlOptions: { position: google.maps.ControlPosition.TOP_RIGHT }
            });

            this.ngZone.run(() => {
                E2eTestUtils.exposeMapObjectToTests(this.map);
            });
        });
    }

    private resetMapConfigToDefaults() {
        this.ngZone.runOutsideAngular(() => {
            this.map.setCenter({ lat: this.mapLatitude, lng: this.mapLongitude });
            this.map.setZoom(this.mapZoomLevel);
            google.maps.event.trigger(this.map, 'resize');

            this.ngZone.run(() => {
                E2eTestUtils.exposeMapObjectToTests(this.map);
            });
        });
    }
}
